import { isSupabaseConfigured } from "@/lib/env";
import { supabase } from "@/lib/supabase";
import { resolveStoredAssetRef } from "@/features/image-generation";
import type { StoryTargetKind } from "@/types/story";

export const CHAPTER_IMAGES_BUCKET = "chapter-images";

function ensureConfigured() {
  if (!isSupabaseConfigured()) {
    throw new Error("Supabase environment variables are not configured.");
  }
}

function sanitizeFileName(fileName: string) {
  return fileName.replace(/[^a-zA-Z0-9._-]+/g, "-");
}

export function buildStoryImagePath(
  userId: string,
  projectId: string,
  storyId: string,
  targetKind: StoryTargetKind,
  targetId: string,
  fileName: string,
) {
  return `${userId}/${projectId}/${storyId}/${targetKind}/${targetId}/${sanitizeFileName(fileName)}`;
}

export async function uploadStoryImage(
  userId: string,
  projectId: string,
  storyId: string,
  targetKind: StoryTargetKind,
  targetId: string,
  file: File,
) {
  ensureConfigured();
  const path = buildStoryImagePath(userId, projectId, storyId, targetKind, targetId, file.name);

  const { data, error } = await supabase.storage
    .from(CHAPTER_IMAGES_BUCKET)
    .upload(path, file, {
      cacheControl: "3600",
      contentType: file.type || undefined,
      upsert: true,
    });

  if (error) {
    throw error;
  }

  return data.path;
}

export async function deleteStoryImage(path: string) {
  ensureConfigured();
  const resolved = resolveStoredAssetRef(path, CHAPTER_IMAGES_BUCKET);

  if (!resolved) {
    return;
  }

  const { error } = await supabase.storage
    .from(resolved.bucket)
    .remove([resolved.path]);

  if (error) {
    throw error;
  }
}

export async function createStoryImageSignedUrl(path: string) {
  ensureConfigured();
  const resolved = resolveStoredAssetRef(path, CHAPTER_IMAGES_BUCKET);

  if (!resolved) {
    throw new Error("Story image path is missing.");
  }

  const { data, error } = await supabase.storage
    .from(resolved.bucket)
    .createSignedUrl(resolved.path, 60 * 30);

  if (error) {
    throw error;
  }

  return data.signedUrl;
}
